package com.htmlagent.site.components.sections

import com.htmlagent.site.state.ThemeChoice
import com.htmlagent.site.state.ThemePreference
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import com.htmlagent.shared.UiLocale
import com.htmlagent.site.components.widgets.LocaleCode
import com.htmlagent.site.components.widgets.Mark
import com.htmlagent.site.components.widgets.TipSide
import com.htmlagent.site.components.widgets.Tooltip
import com.htmlagent.site.i18n.AppLocale
import com.htmlagent.site.i18n.LocalStrings
import com.htmlagent.site.theme.BriefStartHostStyle
import com.htmlagent.site.state.Identity
import com.htmlagent.site.theme.DrawerAccountStyle
import com.htmlagent.site.theme.DrawerActionStyle
import com.htmlagent.site.theme.DrawerAvatarStyle
import com.htmlagent.site.theme.DrawerStartCardStyle
import com.htmlagent.site.theme.DrawerStartGridStyle
import com.htmlagent.site.theme.DrawerStartIconStyle
import com.htmlagent.site.theme.DrawerToggleRowStyle
import com.htmlagent.site.theme.DrawerWhoStyle
import com.htmlagent.site.theme.SegmentActiveStyle
import com.htmlagent.site.theme.SegmentVariant
import com.htmlagent.site.theme.SegmentedStyle
import com.htmlagent.site.theme.pressed
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.htmlagent.site.theme.BriefStartItemStyle
import com.htmlagent.site.theme.BriefStartMenuStyle
import com.htmlagent.site.theme.IconOnlyStyle
import com.htmlagent.site.theme.Icons
import com.htmlagent.site.theme.RailButtonActiveStyle
import com.htmlagent.site.theme.RailButtonVariant
import com.htmlagent.site.theme.RailItemStyle
import com.htmlagent.site.theme.RailMarkStyle
import com.htmlagent.site.theme.RailStyle
import com.htmlagent.site.theme.Space
import com.htmlagent.site.theme.Tokens
import com.htmlagent.site.theme.current
import com.htmlagent.site.theme.named
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.PlusIcon
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event

/**
 * The left rail: new session, conversation, previous sessions and, at the bottom, the language
 * and the color mode.
 *
 * It only carries what really exists: every icon that does nothing is a promise broken on the
 * click.
 */
@Composable
fun IconRail(
    historyOpen: Boolean,
    onNewSession: () -> Unit,
    onNewGuidedSession: () -> Unit,
    onToggleHistory: () -> Unit,
    onSwitchUser: () -> Unit,
) {
    val t = LocalStrings.current

    Column(
        modifier = RailStyle.toModifier().attrsModifier {
            attr("role", "navigation")
            attr("aria-label", t.rail.navLabel)
            attr("data-history", historyOpen.toString())
        },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(Space.Sm),
    ) {
        // The mark is the way back to the public page. Before it was a link the workspace had
        // no anchor at all: nothing to bookmark, nothing to open in a tab, and no way back to
        // read what the product was without editing the address by hand.
        A(
            href = "/",
            attrs = RailMarkStyle.toModifier().toAttrs { attr("aria-label", t.rail.backToLanding) },
        ) {
            Mark()
        }

        StartMenu(onBlank = onNewSession, onGuided = onNewGuidedSession)
        RailButton(t.rail.conversation, active = !historyOpen, onClick = onToggleHistory) {
            Glyph(Icons.Chat)
        }
        RailButton(t.rail.history, active = historyOpen, onClick = onToggleHistory) {
            Glyph(Icons.History)
        }

        // The switches go at the bottom, away from navigation: they are settings, not places.
        Spacer()

        val locale by AppLocale.current.collectAsState()
        val other = if (locale == UiLocale.SPANISH) UiLocale.ENGLISH else UiLocale.SPANISH
        RailButton(
            label = if (other == UiLocale.ENGLISH) t.rail.switchToEnglish else t.rail.switchToSpanish,
            onClick = { AppLocale.set(other) },
        ) {
            LocaleCode(other)
        }

        val colorMode = ColorMode.current
        RailButton(
            label = if (colorMode.isLight) t.rail.switchToDark else t.rail.switchToLight,
            onClick = { ThemePreference.set(if (colorMode.isLight) ThemeChoice.DARK else ThemeChoice.LIGHT) },
        ) {
            Glyph(if (colorMode.isLight) Icons.Moon else Icons.Sun)
        }

        // Last, and with no confirmation: nothing is destroyed. The sessions stay on the
        // server and typing the same address again brings every one of them back, so a
        // dialog here would be ceremony guarding an undo that already exists.
        RailButton(t.rail.switchUser, onClick = onSwitchUser) { Glyph(Icons.Exit) }
    }
}

/**
 * A rail button, with its tooltip on the right: the rail is barely wider than the icon and the
 * next button sits right below.
 *
 * The accessible name comes in through the modifier, which is how Silk widgets take attributes
 * that are not style; without it a reader announces "button" and nothing else.
 */
/**
 * What the `+` opens: start blank, or start with the assistant.
 *
 * It costs a click on the common path, and it buys the only moment where somebody who does not
 * know the assistant exists is deciding what to do. Escape and a click outside close it, and the
 * focus goes back to the trigger — the same contract as the version menu.
 */
@Composable
private fun StartMenu(onBlank: () -> Unit, onGuided: () -> Unit) {
    val t = LocalStrings.current
    var open by remember { mutableStateOf(false) }
    var trigger by remember { mutableStateOf<HTMLElement?>(null) }
    var host by remember { mutableStateOf<HTMLElement?>(null) }

    fun close() {
        open = false
        trigger?.focus()
    }

    // A click anywhere else closes it. Registered on the document because the click that has to
    // close it is by definition one this subtree never sees.
    DisposableEffect(open) {
        val outside: (Event) -> Unit = { event ->
            val target = event.target as? Node
            if (open && (target == null || host?.contains(target) != true)) open = false
        }
        if (open) document.addEventListener("mousedown", outside)
        onDispose { document.removeEventListener("mousedown", outside) }
    }

    Div(
        BriefStartHostStyle.toModifier().toAttrs {
            ref { el -> host = el as? HTMLElement; onDispose { host = null } }
            onKeyDown { event -> if (event.key == "Escape" && open) close() }
        },
    ) {
        Tooltip(t.rail.newSession, TipSide.Right) {
            Button(
                onClick = { open = !open },
                modifier = (if (open) RailButtonActiveStyle.toModifier() else RailItemStyle.toModifier())
                    .named(t.rail.newSession)
                    .attrsModifier {
                        attr("aria-expanded", open.toString())
                        attr("aria-haspopup", "true")
                        ref { el -> trigger = el as? HTMLElement; onDispose { trigger = null } }
                    },
                variant = RailButtonVariant,
            ) {
                PlusIcon()
            }
        }

        if (open) {
            Div(
                BriefStartMenuStyle.toModifier().toAttrs {
                    attr("role", "group")
                    attr("aria-label", t.rail.startMenu)
                },
            ) {
                StartItem(t.rail.startBlank, t.rail.startBlankHint) { close(); onBlank() }
                StartItem(t.rail.startGuided, t.rail.startGuidedHint) { close(); onGuided() }
            }
        }
    }
}

@Composable
private fun StartItem(name: String, hint: String, onPick: () -> Unit) {
    Button(onClick = { onPick() }, modifier = BriefStartItemStyle.toModifier(), variant = null) {
        Span(Modifier.styleModifier { property("font-weight", "600") }.toAttrs()) { Text(name) }
        Span(
            Modifier
                .styleModifier {
                    property("font-size", "${Tokens.textXs.value()}")
                    property("color", Tokens.muted.value().toString())
                    property("line-height", "1.35")
                    // «mí» was left alone on the last line of the phone's menu.
                    property("text-wrap", "pretty")
                }
                .toAttrs(),
        ) { Text(hint) }
    }
}

@Composable
private fun RailButton(
    label: String,
    onClick: () -> Unit,
    active: Boolean = false,
    icon: @Composable () -> Unit,
) {
    Tooltip(label, TipSide.Right) {
        Button(
            onClick = { onClick() },
            modifier = RailItemStyle.toModifier()
                .then(if (active) RailButtonActiveStyle.toModifier() else Modifier)
                .named(label)
                .current(active),
            variant = RailButtonVariant,
        ) {
            icon()
        }
    }
}

/**
 * An icon of our own, drawn as a mask so it inherits the color of whatever contains it.
 *
 * Only for what Silk does not ship; the rest of the UI uses its icon composables.
 */
@Composable
fun Glyph(icon: String, size: CSSLengthNumericValue = Tokens.iconMd.value()) {
    Span(
        IconOnlyStyle.toModifier()
            .size(size)
            .styleModifier {
                property("mask-image", icon)
                property("-webkit-mask-image", icon)
            }
            .toAttrs { attr("aria-hidden", "true") },
    )
}

/**
 * The two ways to start, as cards, for the phone's drawer: they are the drawer's main action and
 * as plain rows they did not look like one.
 */
@Composable
internal fun DrawerStart(onBlank: () -> Unit, onGuided: () -> Unit) {
    val t = LocalStrings.current
    Div(DrawerStartGridStyle.toModifier().toAttrs()) {
        StartCard(Icons.Pen, t.rail.startBlank, t.rail.startBlankHint, onBlank)
        StartCard(Icons.Spark, t.rail.startGuided, t.rail.startGuidedHint, onGuided)
    }
}

@Composable
private fun StartCard(icon: String, name: String, hint: String, onPick: () -> Unit) {
    Button(onClick = { onPick() }, modifier = DrawerStartCardStyle.toModifier(), variant = null) {
        Span(DrawerStartIconStyle.toModifier().toAttrs()) { Glyph(icon, size = Tokens.iconMd.value()) }
        Span(Modifier.styleModifier { property("font-weight", "600") }.toAttrs()) { Text(name) }
        Span(
            Modifier
                .styleModifier {
                    property("font-size", "${Tokens.textXs.value()}")
                    property("color", Tokens.muted.value().toString())
                    property("line-height", "1.35")
                    property("text-wrap", "pretty")
                }
                .toAttrs(),
        ) { Text(hint) }
    }
}

/**
 * The foot of the phone's drawer: who this is, the language and the color mode (with auto) as
 * segmented switches, and leaving.
 */
@Composable
internal fun DrawerAccount(identity: String, onSwitchUser: () -> Unit) {
    val t = LocalStrings.current
    val guest = Identity.isGuest(identity)

    Div(DrawerAccountStyle.toModifier().toAttrs()) {
        Div(DrawerWhoStyle.toModifier().toAttrs()) {
            Span(DrawerAvatarStyle.toModifier().toAttrs { attr("aria-hidden", "true") }) {
                Text(if (guest) "?" else identity.take(1))
            }
            Span { Text(if (guest) t.history.guestName else identity) }
        }

        val locale by AppLocale.current.collectAsState()
        ToggleRow(t.rail.language) {
            TwoWay("ES", locale == UiLocale.SPANISH, { AppLocale.set(UiLocale.SPANISH) })
            TwoWay("EN", locale == UiLocale.ENGLISH, { AppLocale.set(UiLocale.ENGLISH) })
        }

        val choice by ThemePreference.current.collectAsState()
        ToggleRow(t.rail.theme) {
            TwoWay(t.rail.lightMode, choice == ThemeChoice.LIGHT, { ThemePreference.set(ThemeChoice.LIGHT) })
            TwoWay(t.rail.darkMode, choice == ThemeChoice.DARK, { ThemePreference.set(ThemeChoice.DARK) })
            TwoWay(t.rail.autoMode, choice == ThemeChoice.AUTO, { ThemePreference.set(ThemeChoice.AUTO) })
        }

        Button(onClick = { onSwitchUser() }, modifier = DrawerActionStyle.toModifier(), variant = null) {
            Glyph(Icons.Exit, size = Tokens.iconSm.value())
            Text(t.rail.switchUser)
        }
    }
}

@Composable
private fun ToggleRow(label: String, options: @Composable () -> Unit) {
    Div(DrawerToggleRowStyle.toModifier().toAttrs()) {
        Span { Text(label) }
        Row(SegmentedStyle.toModifier().attrsModifier { attr("role", "group"); attr("aria-label", label) }) {
            options()
        }
    }
}

@Composable
private fun TwoWay(text: String, on: Boolean, onPick: () -> Unit) {
    Button(
        onClick = { onPick() },
        modifier = (if (on) SegmentActiveStyle.toModifier() else Modifier).pressed(on),
        variant = SegmentVariant,
        size = ButtonSize.SM,
    ) {
        Text(text)
    }
}
